import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import SearchField from "../../interface/shared/searchfield";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DashboardLayout from "../layouts/dashboard";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import { CreateProductPayload, ProviderProducts, useProducts } from "../../api/products";
import { useSuppliers } from "../../api/suppliers";
import { Supplier } from "../../shared/types";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import { FadeIn } from "../../interface/shared/animations";
import { WeightDisplay } from "../../interface/shared/weightdisplay";

export default function Products() {
    return (
        <DashboardLayout>
            <ProviderProducts>
                <ProductsContent />
            </ProviderProducts>
        </DashboardLayout>
    );
}

function ProductsContent() {
    const {
        products,
        errors,
        loading,
        fetchProducts,
        page,
        perPage,
        total,
        setPage,
        setPerPage,
        searchTerm,
        setSearchTerm,
        includeInactive,
        setIncludeInactive,
        setSupplierId,
        sortField,
        sortOrder,
        setSort,
    } = useProducts();
    const {
        suppliers,
        loading: suppliersLoading,
        setSearchTerm: setSupplierSearchTerm,
        ensureLoaded: ensureSuppliersLoaded,
    } = useSuppliers();
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const navigate = useNavigate();

    const supplierOptions =
        selectedSupplier && !suppliers.some((s) => s.id === selectedSupplier.id)
            ? [selectedSupplier, ...suppliers]
            : suppliers;

    const handleSupplierChange = (_: unknown, value: Supplier | null) => {
        setSelectedSupplier(value);
        setSupplierId(value?.id ?? null);
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Box>
                    <Typography variant="h5" gutterBottom>
                        Products
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        All products in the warehouse.
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                    <Autocomplete
                        options={supplierOptions}
                        loading={suppliersLoading}
                        value={selectedSupplier}
                        onChange={handleSupplierChange}
                        onOpen={ensureSuppliersLoaded}
                        onInputChange={(_, value, reason) => {
                            if (reason === "input" || reason === "clear") {
                                setSupplierSearchTerm(value);
                            }
                        }}
                        getOptionLabel={(option) =>
                            option.code ? `${option.name} (${option.code})` : option.name
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        filterOptions={(options) => options}
                        sx={{
                            minWidth: { xs: "100%", sm: 260 },
                            "& .MuiIconButton-root": {
                                border: "none",
                                background: "none",
                                backgroundColor: "transparent",
                                "&:hover": {
                                    background: "none",
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Filter by supplier"
                                hiddenLabel
                                label=""
                                size="small"
                            />
                        )}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={includeInactive}
                                onChange={(e) => setIncludeInactive(e.target.checked)}
                                size="small"
                            />
                        }
                        label="Show inactive"
                        sx={{ mr: 0, whiteSpace: "nowrap" }}
                    />
                    <SearchField
                        placeholder="Search products by name, barcode, code or main code"
                        value={searchTerm}
                        onChange={setSearchTerm}
                    />
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => setCreateOpen(true)}
                    >
                        Add product
                    </Button>
                </Stack>
            </Stack>

            <CreateProductDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(productId) => navigate(`/products/${productId}`)}
            />
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button startIcon={<RefreshRoundedIcon />} onClick={() => fetchProducts()}>
                            Retry
                        </Button>
                    }
                >
                    {errors.join(" ")}
                </Alert>
            )}
            <Card variant="outlined">
                <CardContent>
                    {loading ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Main Code</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Size</TableCell>
                                    <TableCell>Pack</TableCell>
                                    <TableCell>Weight</TableCell>
                                    <TableCell align="center">Active</TableCell>
                                    <TableCell align="right"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell><Skeleton variant="text" /></TableCell>
                                        <TableCell align="center">
                                            <Box alignItems="center" justifyContent="center" display="flex"><Skeleton variant="circular" width={20} height={20} /></Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box alignItems="center" justifyContent="end" display="flex">
                                                <Skeleton variant="rounded" width={70} height={30} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <FadeIn>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sortDirection={sortField === "code" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "code"}
                                                direction={sortField === "code" ? sortOrder : "asc"}
                                                onClick={() => setSort("code")}
                                            >
                                                Code
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Main Code</TableCell>
                                        <TableCell>Product</TableCell>
                                        <TableCell sortDirection={sortField === "size" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "size"}
                                                direction={sortField === "size" ? sortOrder : "asc"}
                                                onClick={() => setSort("size")}
                                            >
                                                Size
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "pack" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "pack"}
                                                direction={sortField === "pack" ? sortOrder : "asc"}
                                                onClick={() => setSort("pack")}
                                            >
                                                Pack
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sortDirection={sortField === "weight" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "weight"}
                                                direction={sortField === "weight" ? sortOrder : "asc"}
                                                onClick={() => setSort("weight")}
                                            >
                                                Weight
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="center" sortDirection={sortField === "active" ? sortOrder : false}>
                                            <TableSortLabel
                                                active={sortField === "active"}
                                                direction={sortField === "active" ? sortOrder : "asc"}
                                                onClick={() => setSort("active")}
                                            >
                                                Active
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {products.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8}>
                                                <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                                                    No products found.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : products.map((product) => (
                                        <TableRow key={`${product.name}`}>
                                            <TableCell>{product.code}</TableCell>
                                            <TableCell>{product.main_code}</TableCell>
                                            <TableCell>{product.name}</TableCell>
                                            <TableCell>{product.size}</TableCell>
                                            <TableCell>{product.pack}</TableCell>
                                            <TableCell><WeightDisplay value={product.weight} /></TableCell>
                                            <TableCell align="center">
                                                {product.active ? (
                                                    <CheckCircleRoundedIcon color="success" fontSize="small" />
                                                ) : (
                                                    <CancelRoundedIcon color="error" fontSize="small" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<VisibilityRoundedIcon fontSize="small" />}
                                                    onClick={() => navigate(`/products/${product.id}`)}
                                                >
                                                    View
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page - 1}
                                onPageChange={(_, newPage) => setPage(newPage + 1)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => {
                                    setPerPage(parseInt(e.target.value, 10));
                                    setPage(1);
                                }}
                                rowsPerPageOptions={[10, 15, 25, 50]}
                            />
                        </FadeIn>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

function CreateProductDialog({
    open,
    onClose,
    onCreated,
}: {
    open: boolean;
    onClose: () => void;
    onCreated: (productId: number) => void;
}) {
    const { createProduct, creating } = useProducts();
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [mainCode, setMainCode] = useState("");
    const [barcode, setBarcode] = useState("");
    const [size, setSize] = useState("");
    const [pack, setPack] = useState("");
    const [weight, setWeight] = useState("");
    const [uoi, setUoi] = useState("");
    const [price, setPrice] = useState("");
    const [vatCode, setVatCode] = useState<"A" | "B">("A");
    const [active, setActive] = useState(true);
    const [localError, setLocalError] = useState<string | null>(null);
    const [apiError, setApiError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            setName("");
            setCode("");
            setMainCode("");
            setBarcode("");
            setSize("");
            setPack("");
            setWeight("");
            setUoi("");
            setPrice("");
            setVatCode("A");
            setActive(true);
            setLocalError(null);
            setApiError(null);
        }
    }, [open]);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setLocalError(null);
        setApiError(null);

        const trimmedName = name.trim();
        const trimmedCode = code.trim();
        if (!trimmedName) {
            setLocalError("Name is required.");
            return;
        }
        if (!trimmedCode) {
            setLocalError("Code is required.");
            return;
        }

        const parsedWeight = weight.trim() === "" ? 0 : Number(weight);
        if (Number.isNaN(parsedWeight) || parsedWeight < 0) {
            setLocalError("Enter a valid weight.");
            return;
        }

        let parsedUoi: number | null = null;
        if (uoi.trim() !== "") {
            parsedUoi = Number(uoi);
            if (!Number.isInteger(parsedUoi) || parsedUoi < 1) {
                setLocalError("Enter a valid UOI (whole number, 1 or more).");
                return;
            }
        }

        let parsedPrice: number | null = null;
        if (price.trim() !== "") {
            parsedPrice = Number(price);
            if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
                setLocalError("Enter a valid price.");
                return;
            }
        }

        const payload: CreateProductPayload = {
            name: trimmedName,
            code: trimmedCode,
            main_code: mainCode.trim(),
            barcode: barcode.trim(),
            size: size.trim(),
            pack: pack.trim(),
            weight: parsedWeight,
            uoi: parsedUoi,
            price: parsedPrice,
            vat_code: vatCode,
            active,
        };

        const result = await createProduct(payload);
        if (result.ok) {
            onClose();
            onCreated(result.product.id);
        } else {
            setApiError(result.message);
        }
    }

    return (
        <Dialog open={open} onClose={creating ? undefined : onClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Add product</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Creates a warehouse-only product that is not linked to the ERP. These products can be deleted later.
                    </Typography>
                    {(localError || apiError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {localError ?? apiError}
                        </Alert>
                    )}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid size={12}>
                            <TextField
                                label="Name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                required
                                size="small"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Code"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                fullWidth
                                required
                                size="small"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Main code"
                                value={mainCode}
                                onChange={(e) => setMainCode(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                label="Barcode"
                                value={barcode}
                                onChange={(e) => setBarcode(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label="Size"
                                value={size}
                                onChange={(e) => setSize(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label="Pack"
                                value={pack}
                                onChange={(e) => setPack(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                                label="Weight"
                                type="number"
                                value={weight}
                                onChange={(e) => setWeight(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                                slotProps={{ htmlInput: { min: 0, step: "any" } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="UOI"
                                type="number"
                                value={uoi}
                                onChange={(e) => setUoi(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                                slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Price"
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                fullWidth
                                size="small"
                                disabled={creating}
                                slotProps={{ htmlInput: { min: 0, step: "0.01" } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="VAT code"
                                select
                                value={vatCode}
                                onChange={(e) => setVatCode(e.target.value as "A" | "B")}
                                fullWidth
                                required
                                size="small"
                                disabled={creating}
                            >
                                <MenuItem value="A">A</MenuItem>
                                <MenuItem value="B">B</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={active}
                                        onChange={(e) => setActive(e.target.checked)}
                                        disabled={creating}
                                        size="small"
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button type="button" onClick={onClose} disabled={creating}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={creating}>
                        {creating ? "Creating…" : "Create product"}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}